/** Metric input fields, shared by the log form and the edit-workout modal. */
import { maybe } from '../dom';
import { formatTime, parseTime } from '../lib/format';
import type { MetricKey, Workout } from '../types';

export function buildMetricField(
  metric: MetricKey,
  idPrefix: string,
  compact: boolean,
): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.style.marginBottom = compact ? '20px' : '15px';

  const label = document.createElement('label');
  label.htmlFor = `${idPrefix}${metric}-input`;
  label.style.display = 'block';
  label.style.marginBottom = '10px';
  label.style.fontWeight = '600';
  if (!compact) label.style.color = '#555';

  const field = document.createElement('input');
  field.type = metric === 'time' ? 'text' : 'number';
  field.id = `${idPrefix}${metric}-input`;
  field.style.width = '100%';
  field.style.padding = '15px';
  field.style.border = '2px solid #e0e0e0';
  field.style.borderRadius = '10px';
  field.style.fontSize = compact ? '16px' : '18px';
  if (!compact) {
    field.style.textAlign = 'center';
    field.style.transition = 'border-color 0.3s';
  }
  if (metric !== 'time') field.min = '0';

  switch (metric) {
    case 'reps':
      label.textContent = compact ? 'Reps:' : 'Reps Completed:';
      field.placeholder = 'Enter reps';
      break;
    case 'weight':
      label.textContent = 'Weight (lbs):';
      field.placeholder = 'Enter weight';
      field.step = '0.5';
      break;
    case 'time':
      label.textContent = 'Time (mm:ss):';
      field.placeholder = 'Enter time (e.g., 2:30)';
      break;
  }

  wrapper.append(label, field);
  return wrapper;
}

/** Reads and validates one metric input; null when empty or invalid. */
export function readMetricInput(prefix: string, metric: MetricKey): number | string | null {
  const field = maybe<HTMLInputElement>(`${prefix}${metric}-input`);
  const raw = field?.value.trim();
  if (!raw) return null;

  if (metric === 'time') {
    const seconds = parseTime(raw);
    if (seconds === null) return null;
    return raw.includes(':') ? raw : formatTime(seconds);
  }

  const value = Number.parseFloat(raw);
  return Number.isFinite(value) && value > 0 ? value : null;
}

export function applyMetric(target: Workout, metric: MetricKey, value: number | string): void {
  if (metric === 'time') target.time = String(value);
  else if (metric === 'reps') target.reps = Number(value);
  else target.weight = Number(value);
}
